import { yupResolver } from '@hookform/resolvers/yup';
import { GridRowParams } from '@material-ui/data-grid';
import { format } from 'date-fns';
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import useAuth from '~/hooks/useAuth';
import { moneyFormat, transformAsCurrency } from '~/utils/functions';
import { ajusteDeEstoqueServices } from '../services';
import { getInfos } from '../services/getInfos';
import {
  AjusteDeEstoqueContextData,
  AjusteDeEstoqueContextProvider,
} from '../types/context';
import {
  FormDisabledProps,
  ProdutoProps,
  TipoAjusteProps,
  formType,
} from '../types';
import { calculaQtdEst } from '../utils/calculaQtdEst';
import { formataResultado } from '../utils/formataResultado';
import { schemaAjusteEstoque } from '../validations';
import { defaultFormDisabled, defaultTipoAjuste } from '../defaultData';

const MySwal = withReactContent(Swal);

export const AjusteDeEstoqueContext = createContext(
  {} as AjusteDeEstoqueContextData,
);

export const AjusteDeEstoqueProvider: React.FC<
  AjusteDeEstoqueContextProvider
> = ({ children }) => {
  const user = useAuth();

  const [loader, setLoader] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(true);
  const [isInvalidProduto, setIsInvalidProduto] = useState(false);
  const [selectTipoAjustes] = useState<TipoAjusteProps[]>([]);
  const [isDisableSearch, setIsDisableSearch] = useState(false);
  const [produto, setProduto] = useState<ProdutoProps | any>(undefined);
  const [qtdAlteracao, setQtdAlteracao] = useState<any>(0);
  const [estPosterior, setEstPosterior] = useState<any>(0);
  const [tipoUnidadeAjuste, setTipoUnidadeAjuste] = useState<
    number | undefined
  >(undefined);

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [formDisabled, setFormDisabled] =
    useState<FormDisabledProps>(defaultFormDisabled);

  const buscaPrdutoRef = useRef<HTMLDivElement | null>(null);
  const ajusteRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(schemaAjusteEstoque),
  });

  useEffect(() => {
    setValue('cod_loja', user?.user?.loja);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user?.loja]);

  const formTela: formType = {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  };

  const watchManterDados = watch('manterDados');
  const watchManterProduto = watch('manterProduto');

  const focus = async (idOrName: string) => {
    let input = document.getElementById(idOrName);
    if (!input) input = document.querySelector(`[name="${idOrName}"]`);
    setTimeout(() => {
      input?.click();
      input?.focus();
    }, 200);
  };

  const focusOnNextField = () => {
    if (ajusteRef.current) {
      const inputAjuste: HTMLInputElement | null =
        ajusteRef.current.querySelector('[id="ajuste"]');
      if (inputAjuste && inputAjuste.value.length <= 0) {
        inputAjuste.focus();
        inputAjuste.click();
      } else if (inputAjuste && inputAjuste.value.length > 0) {
        focus('qtd_alteracao');
      }
    }
  };

  const onRowClick = async (param: GridRowParams) => {
    const { row } = param;
    setUpdate(true);
    setIsDisabled(true);
    const {
      cod_seq_ajuste_estoque,
      cod_produto,
      cod_loja,
      tipo_operacao,
      tipo_operacao_view,
      cod_tipo_ajuste,
      qtd_ajuste,
      num_documento,
      qtd_estoque_ant,
      qtd_estoque_post,
      flg_cancelado,
      des_produto,
    } = row;

    const tipoAjusteSelecionado = {
      value: tipo_operacao,
      label: `${cod_tipo_ajuste} - ${tipo_operacao_view}`,
      tipo_operacao: undefined,
    };

    setValue('num_documento', num_documento);
    setValue('ajuste', tipoAjusteSelecionado);
    setValue('cod_loja', cod_loja);
    setValue('cod_tipo_ajuste', tipoAjusteSelecionado);
    setValue('cod_seq_ajuste_estoque', cod_seq_ajuste_estoque);
    setValue('qtd_estoque_ant', qtd_estoque_ant);
    setValue('qtd_estoque_post', qtd_estoque_post);
    setValue('flg_cancelado', flg_cancelado);
    setValue('des_produto', des_produto);

    const qtdEstoquePost = qtd_estoque_post.toString().replace('.', ',');
    const qtdAjuste = qtd_ajuste.toString().replace('.', ',');

    setFormDisabled({ loja: true, produto: true });

    const data = await getInfos.getProdutoByCodProduto(cod_produto, cod_loja);

    if (data.success) {
      setProduto(data.data[0]);
      setTipoUnidadeAjuste(
        selectTipoAjustes.find((tp) => tp.cod_tipo_ajuste === cod_tipo_ajuste)
          ?.tipo_unidade ?? undefined,
      );
    }

    setEstPosterior(
      qtd_estoque_post.toString().includes('.')
        ? qtdEstoquePost
        : Math.trunc(qtdEstoquePost),
    );
    setValue(
      'qtd_posterior',
      qtd_estoque_post.toString().includes('.')
        ? qtdEstoquePost
        : Math.trunc(qtdEstoquePost),
    );

    if (!data.data[0]?.flg_ipv) {
      setValue('qtd_alteracao', Math.trunc(qtd_ajuste));
      setQtdAlteracao(Math.trunc(qtd_ajuste));
    } else {
      setValue('qtd_alteracao', qtdAjuste);
      setQtdAlteracao(qtdAjuste);
    }

    setLoader(false);
    setShowSearch(false);
  };

  const resetFormData = async () => {
    const { manterDados, manterProduto } = getValues();
    const produtoEstPosterior = produto;

    if (manterProduto && !manterDados) {
      produtoEstPosterior.qtd_est_atual = transformAsCurrency(estPosterior);
      setProduto(produtoEstPosterior);
      setValue('cod_tipo_ajuste', defaultTipoAjuste);
      setValue('num_documento', '');
      setIsDisabled(true);
      setQtdAlteracao(0);
      setEstPosterior(0);
      setValue('ajuste', '');

      setTimeout(() => {
        focusOnNextField();
      }, 255);

      await focus('ajuste');
      return;
    }
    if (!manterProduto && manterDados) {
      setValue('cod_loja', user.user?.loja);
      setFormDisabled((prev) => ({ ...prev, loja: false, produto: true }));
      setIsDisabled(false);
      setProduto(undefined);
      setIsDisableSearch(false);
      setValue('produtos', { value: undefined, label: '' });
      clearErrors('qtd_alteracao');
      setValue('qtd_alteracao', 0);
      setValue('qtd_posterior', 0);
      setQtdAlteracao(0);
      setEstPosterior(0);
      setTimeout(() => {
        focus('produtos');
      }, 10);
      return;
    }
    if (!manterProduto && !manterDados) {
      setFormDisabled((prev) => ({ ...prev, loja: false, produto: true }));
      setIsDisabled(true);
      setValue('qtd_alteracao', 0);
      setValue('qtd_posterior', 0);
      setQtdAlteracao(0);
      setEstPosterior(0);
      setProduto(undefined);
      setIsDisableSearch(false);
      reset();
      setValue('cod_loja', user.user?.loja);
      setValue('cod_tipo_ajuste', defaultTipoAjuste);
      setValue('num_documento', '');
      setValue('manterDados', false);
      setValue('manterProduto', false);

      setValue('produtos', { value: undefined, label: '' });
      setTimeout(() => {
        focus('produtos');
      }, 10);
      return;
    }
    if (manterProduto && manterDados) {
      produtoEstPosterior.qtd_est_atual = transformAsCurrency(estPosterior);
      setProduto(produtoEstPosterior);
      setQtdAlteracao(0);
      setTimeout(() => {
        focus('qtd_alteracao');
      }, 255);
      return;
    }
    setValue('ajuste', '');
    setValue('qtd_alteracao', undefined);
    setValue('qtd_posterior', undefined);
    setQtdAlteracao(0);
    setEstPosterior(0);
    setTipoUnidadeAjuste(undefined);
  };

  const resetAllFormData = () => {
    setFormDisabled((prev) => ({ ...prev, loja: false, produto: true }));
    setProduto(undefined);
    setValue('cod_loja', user.user?.loja);
    setValue('cod_seq_ajuste_estoque', undefined);
    setValue('cod_tipo_ajuste', defaultTipoAjuste);
    setValue('ajuste', '');
    setValue('manterDados', false);
    setValue('manterProduto', false);
    setValue('qtd_alteracao', 0);
    setValue('qtd_posterior', 0);
    setValue('qtd_estoque_ant', 0);
    setValue('qtd_estoque_post', undefined);
    setValue('num_documento', '');
    setValue('flg_cancelado', false);
    setValue('des_produto', '');
    setQtdAlteracao(0);
    setEstPosterior(0);
    setIsInvalidProduto(false);
    setIsDisableSearch(false);
    setIsDisabled(true);
    setTipoUnidadeAjuste(undefined);
  };

  const handleLoja = useCallback(
    (value: number) => {
      setValue('cod_loja', value);
      setFormDisabled((prev) => ({ ...prev, produto: false }));
    },
    [setValue],
  );

  const handleTipoAjuste = useCallback(
    (tipoAjuste: TipoAjusteProps) => {
      const val = {
        label: tipoAjuste.label,
        value: tipoAjuste.value,
        tipo_operacao: tipoAjuste.tipo_operacao,
      };

      setIsDisabled(false);
      const valueFormatado = moneyFormat(
        produto?.qtd_est_atual.toString(),
        3,
        3,
      );
      if (!produto?.qtd_est_atual.toString().includes('.')) {
        setEstPosterior(produto?.qtd_est_atual);
        setValue('qtd_posterior', produto?.qtd_est_atual);
      } else {
        setValue('qtd_posterior', valueFormatado);
        setEstPosterior(valueFormatado);
      }
      setValue('cod_tipo_ajuste', val);
      setValue('qtd_alteracao', 0);
      setQtdAlteracao(0);
      if (!produto?.qtd_est_atual.toString().includes('.')) {
        setEstPosterior(produto?.qtd_est_atual);
        setValue('qtd_posterior', produto?.qtd_est_atual);
        return;
      }
      setEstPosterior(valueFormatado);
      setValue('qtd_posterior', valueFormatado);
    },
    [produto, setValue],
  );

  const handleNumDocumento = useCallback(
    (val: string) => {
      setValue('num_documento', val);
    },
    [setValue],
  );

  const handleAddProduto = async (prod: any[]) => {
    const { manterDados } = getValues();

    if (prod.length === 0) {
      return toast.warning('Nenhum produto atende o critério de busca');
    }
    setProduto(prod[0]);
    const precoFormatted = !prod[0]?.val_preco
      ? '0,00'
      : moneyFormat(prod[0]?.val_preco?.toString(), 2, 2);

    setValue('produtos', {
      value: prod[0].cod_produto,
      label: `${prod[0].cod_produto} - ${
        prod[0].des_produto
      }| Loja: ${prod[0]?.cod_loja?.toString()} | Venda: ${precoFormatted} | Estq.: ${moneyFormat(
        prod[0]?.qtd_total_estoque?.toString(),
        3,
        3,
      )} |`,
    });
    setFormDisabled((prev) => ({ ...prev, loja: true, produto: true }));
    setIsInvalidProduto(false);
    if (prod.length > 0) {
      setIsDisableSearch(true);
    }

    const product = prod[0];
    const isAssociado =
      product.cod_associado !== null &&
      typeof product.cod_associado === 'string';

    const handleConfirmation = async () => {
      const prodAssociado = await getInfos.getProdutoPai(
        product.cod_associado,
        product.cod_loja,
      );

      if (manterDados) {
        setQtdAlteracao(0);
        setValue(
          'qtd_posterior',
          prodAssociado.qtd_est_atual.toString().replace('.', ','),
        );
        setEstPosterior(
          prodAssociado.qtd_est_atual.toString().replace('.', ','),
        );
      }

      setProduto(prodAssociado);
      setFormDisabled((prev) => ({ ...prev, loja: true, produto: true }));
      setIsInvalidProduto(false);

      if (prodAssociado !== null) setIsDisableSearch(true);
    };

    if (isAssociado) {
      await MySwal.fire({
        title: 'Confirmação',
        text: 'Não é permitido fazer Ajuste de Estoque em Item Associado. Deseja pesquisar o produto pai?',
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonColor: '#28A745',
        cancelButtonColor: '#DC3545',
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não',
        icon: 'question',
      }).then(async (result) => {
        if (result.isConfirmed) {
          await handleConfirmation();

          setTimeout(() => {
            focusOnNextField();
          }, 255);
        } else {
          resetAllFormData();
          setUpdate(false);
          setShowSearch(false);
          setTimeout(() => {
            focus('produtos');
          }, 255);
        }
      });
    } else {
      setProduto(product);
      setFormDisabled((prev) => ({ ...prev, loja: true, produto: true }));
      setIsInvalidProduto(false);
      if (prod.length > 0) {
        setIsDisableSearch(true);
      }

      if (manterDados) {
        setQtdAlteracao(0);
        setEstPosterior(product.qtd_est_atual.toString().replace('.', ','));
        setValue(
          'qtd_posterior',
          product.qtd_est_atual.toString().replace('.', ','),
        );
        focus('qtd_alteracao');
      } else {
        focus('ajuste');
      }
    }
  };

  const handleDeleteProduto = async () => {
    const { manterDados } = getValues();
    setProduto(undefined);
    setIsDisableSearch(false);
    setIsDisabled(true);
    setFormDisabled((prev) => ({ ...prev, loja: false, produto: false }));
    setValue('produtos', { value: '', label: '' });
    if (!manterDados) {
      setValue('num_documento', '');
      setValue('cod_tipo_ajuste', defaultTipoAjuste);
    }
    setValue('qtd_alteracao', 0);
    setValue('qtd_posterior', 0);
    setQtdAlteracao(0);
    setEstPosterior(0);
    setTipoUnidadeAjuste(undefined);

    await focus('input-produto');
  };

  const formataValorCampoQuantidade = useCallback(
    (value: number | string) => {
      const valorNumerico =
        typeof value === 'string' ? transformAsCurrency(value) : Number(value);

      if (Number.isNaN(valorNumerico)) {
        return produto?.flg_ipv ? '0,000' : '0';
      }

      if (produto?.flg_ipv) {
        return formataResultado(valorNumerico);
      }

      return Math.trunc(valorNumerico).toString();
    },
    [produto?.flg_ipv],
  );

  const handleQtdAlteracao = useCallback(
    (val: any) => {
      const quantidadeAlteracao: number = val;
      const qtdAlteracaoFormatada =
        formataValorCampoQuantidade(quantidadeAlteracao);
      const tipoOperacao = getValues('cod_tipo_ajuste')?.tipo_operacao;
      const operacao = tipoOperacao === 0 ? 'soma' : 'subtracao';
      const estoquePosterior = calculaQtdEst(
        operacao,
        produto,
        Number(quantidadeAlteracao),
        Number(tipoUnidadeAjuste),
      );
      const estoquePosteriorSemMilhar = formataResultado(
        estoquePosterior,
      ).replace(/\./g, '');
      const estoquePosteriorNumerico = parseFloat(
        estoquePosteriorSemMilhar.replace(',', '.'),
      );
      const estoquePosteriorFormatado = formataValorCampoQuantidade(
        estoquePosteriorNumerico,
      );

      setValue('qtd_alteracao', qtdAlteracaoFormatada);
      setQtdAlteracao(qtdAlteracaoFormatada);
      setEstPosterior(estoquePosteriorFormatado);
      setValue('qtd_posterior', estoquePosteriorFormatado);
    },
    [
      getValues,
      produto,
      tipoUnidadeAjuste,
      formataValorCampoQuantidade,
      setValue,
    ],
  );

  const handleEstoquePosterior = useCallback(
    (estoquePosterior: any) => {
      const estoquePosteriorFormatado =
        formataValorCampoQuantidade(estoquePosterior);
      const tipoOperacao = getValues('cod_tipo_ajuste')?.tipo_operacao;
      const quantidadeAlteracao =
        tipoOperacao === 0
          ? estoquePosterior - Number(produto?.qtd_est_atual)
          : Number(produto?.qtd_est_atual) - estoquePosterior;
      const qtdAlteracaoSemMilhar = formataResultado(
        quantidadeAlteracao,
      ).replace(/\./g, '');
      const qtdAlteracaoNumerica = parseFloat(
        qtdAlteracaoSemMilhar.replace(',', '.'),
      );
      const qtdAlteracaoFormatada = formataValorCampoQuantidade(
        Number.isNaN(qtdAlteracaoNumerica) ? 0 : qtdAlteracaoNumerica,
      );

      setEstPosterior(estoquePosteriorFormatado);
      setValue('qtd_posterior', estoquePosteriorFormatado);
      setValue('qtd_alteracao', qtdAlteracaoFormatada);
      setQtdAlteracao(qtdAlteracaoFormatada);
    },
    [getValues, produto?.qtd_est_atual, formataValorCampoQuantidade, setValue],
  );

  const formatNumber = (value: number | undefined | null) => {
    if (value === undefined || value === null) {
      return '';
    }
    return formataResultado(value);
  };

  const onSave = handleSubmit(async (dataForm) => {
    try {
      if (produto === undefined) {
        setIsInvalidProduto(true);
        return;
      }
      setIsInvalidProduto(false);

      if (!produto?.flg_ipv && !Number.isInteger(Number(qtdAlteracao))) {
        toast.warn('Apenas produtos de peso variável podem ser fracionados.');
        setTimeout(() => {
          focus('qtd_alteracao');
        }, 200);
        return;
      }

      setLoader(true);
      const data = await ajusteDeEstoqueServices.createAjuste({
        cod_produto: produto.cod_produto,
        cod_loja: dataForm.cod_loja,
        cod_tipo_ajuste: dataForm.cod_tipo_ajuste?.value,
        qtd_ajuste: transformAsCurrency(dataForm.qtd_alteracao),
        num_documento: dataForm.num_documento,
        qtd_estoque_ant:
          produto.qtd_est_atual !== null ? produto.qtd_est_atual : 0,
        qtd_estoque_post: transformAsCurrency(dataForm.qtd_posterior),
        dta_ajuste: format(new Date(), 'yyy-MM-dd'),
        val_custo_rep: produto?.val_custo_rep,
      });

      if (data.success) {
        toast.success(data.message);
        await resetFormData();
      }
    } finally {
      setLoader(false);
    }
  });

  const handleCancelarAjuste = async () => {
    const { flg_cancelado } = getValues();

    if (flg_cancelado) {
      toast.warning('Ajuste já cancelado.');
      return;
    }

    const codSeq = getValues('cod_seq_ajuste_estoque');

    if (!codSeq) {
      toast.warning('Nenhum ajuste selecionado para cancelamento.');
      return;
    }

    try {
      setLoader(true);

      const { message, success } = await ajusteDeEstoqueServices.cancelAjuste(
        codSeq,
      );

      if (!success) {
        toast.warning(message || 'Não foi possível cancelar o ajuste.');
        return;
      }

      setValue('flg_cancelado', true);
      toast.success(message);
    } catch {
      toast.error('Erro ao cancelar o ajuste de estoque.');
    } finally {
      setLoader(false);
    }
  };

  return (
    <AjusteDeEstoqueContext.Provider
      value={{
        formTela,

        loader,
        setLoader,
        showSearch,
        setShowSearch,
        isUpdate,
        setUpdate,

        formDisabled,

        produto,
        qtdAlteracao,
        estPosterior,
        tipoUnidadeAjuste,
        isDisabled,
        isDisableSearch,
        isInvalidProduto,

        watchManterDados,
        watchManterProduto,

        buscaPrdutoRef,
        ajusteRef,

        setTipoUnidadeAjuste,

        handleLoja,
        handleTipoAjuste,
        handleNumDocumento,
        handleAddProduto,
        handleDeleteProduto,
        handleQtdAlteracao,
        handleEstoquePosterior,
        handleCancelarAjuste,

        onRowClick,
        onSave,

        resetAllFormData,
        resetFormData,
        focus,
        formatNumber,
      }}
    >
      {children}
    </AjusteDeEstoqueContext.Provider>
  );
};
